import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { BarChart3, Calendar, RefreshCw } from 'lucide-react';
import { interviewStatsApi, MonthlyCompletionData } from '../../services/api';
import { buildMonthOptions } from '../../utils/monthOptions';

const MONTH_OPTION_COUNT = 36;
// デフォルトの表示期間（月数）。今月だけだと直近の受験実績が見えづらいため、
// 直近3ヶ月分（今月含む）をデフォルトで表示する。
const DEFAULT_RANGE_MONTHS = 3;

const UsageStatsTab: React.FC = () => {
  const monthOptions = useMemo(() => buildMonthOptions(MONTH_OPTION_COUNT), []);
  const currentMonth = monthOptions[0]?.value ?? '';
  const defaultFromMonth = monthOptions[DEFAULT_RANGE_MONTHS - 1]?.value ?? currentMonth;
  const [from, setFrom] = useState(defaultFromMonth);
  const [to, setTo] = useState(currentMonth);
  const [data, setData] = useState<MonthlyCompletionData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = useCallback(async (fromMonth: string, toMonth: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await interviewStatsApi.getMonthlyCompletion({
        from: fromMonth,
        to: toMonth,
      });
      if (response.success && response.data) {
        setData(response.data);
      } else {
        setError(response.message || '月次受験完了者数の取得に失敗しました');
      }
    } catch (err) {
      console.error('Usage stats load error:', err);
      setError('月次受験完了者数の取得中にエラーが発生しました');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (currentMonth) {
      fetchStats(defaultFromMonth, currentMonth);
    }
  }, [currentMonth, defaultFromMonth, fetchStats]);

  const handleApply = () => {
    if (from > to) {
      setError('開始月は終了月以前を指定してください');
      return;
    }
    fetchStats(from, to);
  };

  const periodLabel = data
    ? data.summary.period.from === data.summary.period.to
      ? data.summary.period.fromLabel
      : `${data.summary.period.fromLabel} 〜 ${data.summary.period.toLabel}`
    : '';

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <BarChart3 className="w-5 h-5 text-blue-600" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900">利用状況</h2>
              <p className="text-sm text-gray-500 mt-1">
                面接ステータスが evaluated のセッションを、面接終了日時の月ごとに集計します。
              </p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">開始月</label>
              <select
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                disabled={loading}
                className="w-full sm:w-44 px-3 py-2 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {monthOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">終了月</label>
              <select
                value={to}
                onChange={(e) => setTo(e.target.value)}
                disabled={loading}
                className="w-full sm:w-44 px-3 py-2 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {monthOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <button
              onClick={handleApply}
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  集計中...
                </>
              ) : (
                <>
                  <Calendar className="w-4 h-4" />
                  表示
                </>
              )}
            </button>
          </div>

          {error && (
            <div className="p-4 rounded-md bg-red-50 text-red-800 border border-red-200">
              {error}
            </div>
          )}

          {data && (
            <>
              <div className="rounded-lg border border-blue-100 bg-blue-50 px-5 py-4">
                <p className="text-sm text-blue-800">{periodLabel} の受験完了者数</p>
                <p className="mt-1 text-3xl font-semibold text-blue-900">
                  {data.summary.totalCompleted}
                  <span className="ml-1 text-base font-medium">名</span>
                </p>
              </div>

              <div className="overflow-x-auto border border-gray-200 rounded-lg">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        月
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        受験完了数
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {data.monthly.map((bucket) => (
                      <tr key={`${bucket.year}-${bucket.month}`} className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm text-gray-900">{bucket.label}</td>
                        <td className="px-6 py-4 text-sm text-gray-900 text-right">
                          {bucket.completedCount}名
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-gray-50 border-t border-gray-200">
                      <td className="px-6 py-4 text-sm font-semibold text-gray-900">合計</td>
                      <td className="px-6 py-4 text-sm font-semibold text-gray-900 text-right">
                        {data.summary.totalCompleted}名
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default UsageStatsTab;
